import * as Cesium from "cesium";
import React from "react";
import useViewerStore from "../stores/useViewerStore";
import useYellowEdgeStore from "../stores/useYellowEdgeStore";

const log = (message?: unknown, ...optionalParams: unknown[]) => {
  // eslint-disable-next-line no-console
  console.log("[useModels]", message, ...optionalParams);
};

export default function useModels() {
  const { models, viewer } = useViewerStore();
  const tilesetsRef = React.useRef<Cesium.Cesium3DTileset[]>([]);
  const [loadedFeatures, setLoadedFeatures] = React.useState<
    Cesium.Cesium3DTileFeature[]
  >([]);

  const [egeDetectionStage] = React.useState(
    Cesium.PostProcessStageLibrary.createEdgeDetectionStage(),
  );

  React.useEffect(() => {
    egeDetectionStage.uniforms.color = Cesium.Color.YELLOW;
    egeDetectionStage.uniforms.length = 0.01;
    egeDetectionStage.selected = [];
  }, [egeDetectionStage]);

  const { feature } = useYellowEdgeStore();

  React.useEffect(() => {
    if (!viewer) return () => {};

    viewer.scene.postProcessStages.removeAll();
    viewer.scene.postProcessStages.add(
      Cesium.PostProcessStageLibrary.createSilhouetteStage([egeDetectionStage]),
    );
    return () => {
      viewer.scene.postProcessStages.remove(egeDetectionStage);
    };
  }, [egeDetectionStage, viewer]);

  React.useEffect(() => {
    if (feature && loadedFeatures.length > 0) {
      // eslint-disable-next-line no-underscore-dangle
      const { content } = feature as unknown as {
        content: { tile: { tileset: { _url: string } } };
      };
      if (!content) return;
      // eslint-disable-next-line no-underscore-dangle
      const featureUrl = content.tile.tileset._url || "Unknown URL";
      const matchingFeatures = loadedFeatures.filter((f) => {
        // eslint-disable-next-line no-underscore-dangle
        const { content: loadedContent } = f as unknown as {
          content: { tile: { tileset: { _url: string } } };
        };
        // eslint-disable-next-line no-underscore-dangle
        const loadedFeatureUrl =
          // eslint-disable-next-line no-underscore-dangle
          loadedContent.tile.tileset._url || "Unknown URL";
        return loadedFeatureUrl === featureUrl;
      });

      if (matchingFeatures) {
        egeDetectionStage.selected = matchingFeatures;
        log("Matching feature found:", matchingFeatures);
      } else {
        log("No matching feature found for URL:", featureUrl);
      }
    }

    // if () {
    //   // eslint-disable-next-line no-underscore-dangle
    //   const featureUrl = feature.tileset?.url || "Unknown URL";
    //   console.log(`Feature URL: ${featureUrl}`);
    // }
  }, [loadedFeatures, feature, egeDetectionStage]);

  React.useEffect(() => {
    if (!models || !viewer) return;

    tilesetsRef.current = [];
    setLoadedFeatures([]);

    if (models.length === 0) return;

    models.forEach((model) => {
      log("model set", model.name);
      const { modelPath } = model;
      Cesium.Cesium3DTileset.fromUrl(modelPath, {
        skipLevelOfDetail: true,
        baseScreenSpaceError: 1024,
        skipScreenSpaceErrorFactor: 16,
        skipLevels: 1,
        immediatelyLoadDesiredLevelOfDetail: false,
        loadSiblings: false,
        cullWithChildrenBounds: true,
      }).then((ts) => {
        ts.tileLoad.addEventListener((tile) => {
          if (!tile) return;
          const { content } = tile; // 타일의 콘텐츠를 가져옵니다.
          const { featuresLength } = content; // 타일에 포함된 특징의 수를 가져옵니다.

          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < featuresLength; i++) {
            const f = content.getFeature(i); // 각 특징을 가져옵니다.
            // log("pickedFeature", `Feature ${i}:`, feature); // 특징을 콘솔에 출력합니다.
            setLoadedFeatures((prev) => [...prev, f]);
          }
        });

        // ✅ 투명도 스타일 적용
        if (modelPath.includes("opacity50")) {
          // eslint-disable-next-line no-param-reassign
          ts.style = new Cesium.Cesium3DTileStyle({
            color: {
              conditions: [["true", 'color("white", 0.5)']],
            },
          });
        }

        const tilesetCenter = ts.boundingSphere.center;
        const tilesetCenterCartographic =
          Cesium.Cartographic.fromCartesian(tilesetCenter);
        const adjustedHeight = tilesetCenterCartographic.height;
        const newCenter = Cesium.Cartesian3.fromRadians(
          tilesetCenterCartographic.longitude,
          tilesetCenterCartographic.latitude,
          adjustedHeight,
        );
        const translation = Cesium.Cartesian3.subtract(
          newCenter,
          tilesetCenter,
          new Cesium.Cartesian3(),
        );
        // eslint-disable-next-line no-param-reassign
        ts.modelMatrix = Cesium.Matrix4.fromTranslation(
          translation,
          ts.modelMatrix,
        );

        viewer.scene.primitives.add(ts);
        tilesetsRef.current.push(ts);
      });
    });

    // eslint-disable-next-line consistent-return
    return () => {
      log("cleanup", tilesetsRef.current);
      setLoadedFeatures([]);
      tilesetsRef.current.forEach((tileset) => {
        viewer.scene.primitives.remove(tileset);
      });
      tilesetsRef.current = [];
    };
  }, [models, viewer]);
}